/**
 * Parse reference pattern from text
 *
 * Extracts pattern like: [text](referenceId=value)\nquestion or [text](id=value)\nquestion
 *
 * @example
 * ```ts
 * const result = parseReferencePattern("[你好呀?](referenceId=123)\nBTc");
 * // Returns: {
 * //   referenceContent: "你好呀?",
 * //   key: "referenceId",
 * //   value: "123",
 * //   hasNewline: true,
 * //   question: "BTc"
 * // }
 *
 * const result2 = parseReferencePattern("[Text](id=456)");
 * // Returns: {
 * //   referenceContent: "Text",
 * //   key: "id",
 * //   value: "456",
 * //   hasNewline: false,
 * //   question: undefined
 * // }
 * ```
 */

type QuestionContentResult = {
  referenceContent?: string;
  question: string;
};

export interface ReferencePatternResult {
  /** Text content inside square brackets */
  referenceContent: string;
  /** Key name ("referenceId", "id" or "referenceType") */
  key: string;
  /** Value (e.g., 123) */
  value: string;
  /** Whether contains newline character */
  hasNewline: boolean;
  /** Question text after the pattern */
  question?: string;
}

/**
 * Parse reference pattern from text
 *
 * Pattern format: [text](referenceId=value)\nquestion or [text](id=value)\nquestion
 * Matches when key is "referenceId" or "id"
 *
 * @param text - Input text to parse
 * @returns Parsed result or null if pattern doesn't match
 */
/**
 * Parse reference pattern with relaxed validation
 *
 * Matches when parentheses contain "referenceId=", "id=" or "referenceType="
 * (doesn't require strict format)
 * This allows more flexible formats as long as the key is present
 *
 * @param text - Input text to parse
 * @returns Parsed result or null if pattern doesn't match
 */
export const parseReferencePatternStrict = (
  text: string,
): ReferencePatternResult | null => {
  // Relaxed pattern: only requires that parentheses contain
  // "referenceId=", "id=" or "referenceType="
  // Capture groups: 1=referenceContent, 2=key, 3=value, 4=newline, 5=question
  // Value is captured until space or closing parenthesis (more precise value extraction)
  // Note: [^\]]* allows empty brackets (changed from + to *)
  const pattern =
    /\[([^\]]*)\]\(.*?(referenceId|id|referenceType)=([^\s)]+).*?\)(\n|\\n)?(.+)?/;

  const match = text.match(pattern);

  if (!match) {
    return null;
  }

  return {
    referenceContent: match[1] || "",
    key: match[2] || "",
    value: match[3] || "",
    hasNewline: !!match[4],
    question: match[5],
  };
};

/**
 * 获取解析question内容
 * @param content - Content to parse
 * @returns Question content result
 */
export const getReferencePattern = (content: string): QuestionContentResult => {
  const result = parseReferencePatternStrict(content);
  if (result) {
    return {
      referenceContent: result.referenceContent,
      question: result.question ?? content,
    };
  }
  return {
    referenceContent: undefined,
    question: content,
  };
};
